"use client";

import { EditContactModal } from "@/components/features/contact/AddOrEditContactModal/EditContactModal";
import { LoadingSpinner } from "@/components/ui";
import {
  MatterContactConnectionRichCard,
  type MatterContactConnectionRichCardVisualVariant,
} from "@/components/ui/MatterContactConnectionRichCard/MatterContactConnectionRichCard";
import { useUser } from "@/components/UserProvider";
import { retrieveContactQueryOptions } from "@/lib/data/react-query";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type {
  ConnectionContactPreview,
  Connection,
  Contact,
  MatterContactConnection,
} from "@/lib/types";
import { formatContactsName } from "@/lib/utils/format";
import {
  matterContactConnectionHeroTone,
  matterContactConnectionHeroToneFromMatterConnection,
} from "@/lib/utils/matters/matter-contact-connection-hero-tone";
import { getlabel } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useMemo, useState } from "react";

export interface MatterContactConnectionModalPreviewProps {
  contactId: string;
  /** When the parent already has contact data, skip the suspense fetch. */
  previewContact?: ConnectionContactPreview | Contact | null;
  /** Use the preview for display, but retrieve the complete contact only if editing opens. */
  fetchContactOnEdit?: boolean;
  matterContactConnection?: Connection | MatterContactConnection;
  matterId?: string;
  /** Clears this pick from the form (dirty); same intent as `AddContactFormField` `onDelete`. */
  onRemoveContact?: (contact: Contact) => void;
  /** Called when the embedded edit-contact modal saves updated contact data. */
  onContactUpdated?: (contact: Contact) => void;
  visualVariant?: MatterContactConnectionRichCardVisualVariant;
}

function MatterContactConnectionModalPreviewLoaded({
  contact,
  fetchContactOnEdit,
  matterContactConnection,
  matterId,
  onRemoveContact,
  onContactUpdated,
  visualVariant,
}: {
  contact: ConnectionContactPreview | Contact;
  fetchContactOnEdit?: boolean;
  matterContactConnection?: Connection | MatterContactConnection;
  matterId?: string;
  onRemoveContact?: (contact: Contact) => void;
  onContactUpdated?: (contact: Contact) => void;
  visualVariant?: MatterContactConnectionRichCardVisualVariant;
}) {
  const { hasActionPermission } = useUser();
  const [editContactOpen, setEditContactOpen] = useState(false);

  const slotLabel =
    matterContactConnection && getlabel(matterContactConnection).trim()
      ? getlabel(matterContactConnection).trim()
      : "Contact";

  const { data: matterRes } = useQuery({
    ...retrieveMatterQueryOptions(matterId ?? ""),
    enabled: Boolean(matterId),
  });
  const tone =
    matterRes?.data && matterContactConnection
      ? matterContactConnectionHeroToneFromMatterConnection(
          matterRes.data,
          matterContactConnection as MatterContactConnection,
        )
      : matterContactConnectionHeroTone(slotLabel);

  const cardContact = useMemo(() => {
    if (matterContactConnection) {
      return {
        ...contact,
        contactName:
          formatContactsName(contact) ??
          formatContactsName(matterContactConnection.contact as never) ??
          "",
        connectionId: matterContactConnection.id,
        description: matterContactConnection.description,
        label: getlabel(matterContactConnection),
      };
    }
    return contact;
  }, [contact, matterContactConnection]);

  const hasEditContactPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.contact,
    ACTION_PERMISSION_VALUES.update,
  );

  return (
    <>
      <MatterContactConnectionRichCard
        contact={cardContact}
        matterContactConnection={
          matterContactConnection as MatterContactConnection | undefined
        }
        {...(matterId
          ? { communicationActions: "call-text" as const, matterId }
          : { communicationActions: "call-email" as const })}
        tone={tone}
        visualVariant={visualVariant}
        hideConnectionDescription
        showEditButton={hasEditContactPermission}
        onEdit={
          hasEditContactPermission
            ? () => {
                setEditContactOpen(true);
              }
            : undefined
        }
        onClearSelection={
          onRemoveContact
            ? () => {
                onRemoveContact(contact as Contact);
              }
            : undefined
        }
        className="w-full min-w-0 border-0 p-0 shadow-none"
      />
      <EditContactModal
        formId={`edit-contact-matter-preview-${contact.id}`}
        hasEditPermission={hasEditContactPermission}
        open={editContactOpen}
        setOpen={setEditContactOpen}
        activeContactId={contact.id}
        activeContact={fetchContactOnEdit ? undefined : (contact as Contact)}
        handleSubmit={onContactUpdated}
        matterId={matterId}
        matterContactConnection={
          matterContactConnection as MatterContactConnection | undefined
        }
      />
    </>
  );
}

function MatterContactConnectionModalPreviewSuspenseInner(
  props: Omit<MatterContactConnectionModalPreviewProps, "previewContact">,
) {
  const { data: contactRes } = useSuspenseQuery(
    retrieveContactQueryOptions(props.contactId),
  );
  return (
    <MatterContactConnectionModalPreviewLoaded
      contact={contactRes.data}
      fetchContactOnEdit={props.fetchContactOnEdit}
      matterContactConnection={props.matterContactConnection}
      matterId={props.matterId}
      onRemoveContact={props.onRemoveContact}
      onContactUpdated={props.onContactUpdated}
      visualVariant={props.visualVariant}
    />
  );
}

/**
 * Matter–contact rich card + edit contact modal for split modals and pickers.
 * Wraps a suspense fetch unless `previewContact` is provided.
 */
export function MatterContactConnectionModalPreview({
  previewContact,
  fetchContactOnEdit,
  contactId,
  matterContactConnection,
  matterId,
  onRemoveContact,
  onContactUpdated,
  visualVariant,
}: MatterContactConnectionModalPreviewProps) {
  if (previewContact) {
    return (
      <MatterContactConnectionModalPreviewLoaded
        contact={previewContact}
        fetchContactOnEdit={fetchContactOnEdit}
        matterContactConnection={matterContactConnection}
        matterId={matterId}
        onRemoveContact={onRemoveContact}
        onContactUpdated={onContactUpdated}
        visualVariant={visualVariant}
      />
    );
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <MatterContactConnectionModalPreviewSuspenseInner
        contactId={contactId}
        fetchContactOnEdit={fetchContactOnEdit}
        matterContactConnection={matterContactConnection}
        matterId={matterId}
        onRemoveContact={onRemoveContact}
        onContactUpdated={onContactUpdated}
        visualVariant={visualVariant}
      />
    </Suspense>
  );
}
